import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  TextField,
  toast,
} from '@eon/ui';
import { hooks, type IdTag } from '@eon/api-client';
import { formatDate } from '@eon/utils';

interface ExtendForm {
  expiryDate: string;
}

interface ExtendIdTagExpiryDialogProps {
  tag: IdTag | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExtendIdTagExpiryDialog({ tag, open, onOpenChange }: ExtendIdTagExpiryDialogProps) {
  const { t } = useTranslation();
  const update = hooks.useUpdateIdTag();
  const { register, handleSubmit, reset, formState } = useForm<ExtendForm>({
    defaultValues: { expiryDate: '' },
  });

  useEffect(() => {
    if (tag?.expiresAt && open) {
      reset({ expiryDate: formatDate(tag.expiresAt, 'yyyy-MM-dd') });
    } else if (open) {
      reset({ expiryDate: '' });
    }
  }, [tag, open, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!tag) return;
    if (!values.expiryDate) {
      toast.error(t('idTag.expiryRequired'));
      return;
    }
    try {
      await update.mutateAsync({
        id: tag.id,
        expiryDate: values.expiryDate,
      });
      toast.success(t('idTag.expiryUpdated'));
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {tag?.expiresAt ? t('idTag.extendTitle') : t('idTag.addExpiryTitle')}
          </DialogTitle>
        </DialogHeader>
        {tag ? (
          <div className="bg-muted mb-4 rounded-md p-3 text-sm">
            <p className="font-mono">{tag.name}</p>
            <p className="text-muted-foreground font-mono text-xs">{tag.value}</p>
            {tag.expiresAt ? (
              <p className="mt-2">
                {t('idTag.currentExpiry')}: {formatDate(tag.expiresAt, 'yyyy-MM-dd')}
              </p>
            ) : (
              <p className="mt-2 text-muted-foreground">{t('idTag.noExpiry')}</p>
            )}
          </div>
        ) : null}
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label={t('idTag.newExpiry')}>
            <TextField type="date" {...register('expiryDate', { required: true })} />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={formState.isSubmitting || update.isPending}>
              {t('actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
